import { z } from "zod";

export const costMoneySchema = z.object({ gross: z.number().int().safe(), credits: z.number().int().safe(), net: z.number().int().safe() });
export type CostMoney = z.infer<typeof costMoneySchema>;
export const costEntitySchema = z.object({
  key: z.string().max(100), name: z.string().max(200),
  kind: z.enum(["app", "shared", "unallocated"]),
  total: costMoneySchema,
  daily: z.array(z.object({ day: z.string().date(), ...costMoneySchema.shape })).max(31),
  breakdown: z.array(z.object({ service: z.string().max(200), category: z.string().max(300), ...costMoneySchema.shape })).max(500),
});
export const costMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  coverageStart: z.string().date().nullable(), latestUsageDay: z.string().date().nullable(),
  entities: z.array(costEntitySchema).max(502),
});
export type CostEntity = z.infer<typeof costEntitySchema>;
export type CostMonth = z.infer<typeof costMonthSchema>;
export type CostResource = { appId: string; name: string; suffix: string };
export const costResourceSchema = z.object({ appId: z.string().regex(/^[a-z0-9]+$/i).max(100), name: z.string().max(200), suffix: z.string().regex(/^[a-z0-9]+$/).max(30) });
export const costImportRequestSchema = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/), resources: z.array(costResourceSchema).max(500) });
export const emptyMoney = (): CostMoney => ({ gross: 0, credits: 0, net: 0 });
export function addMoney(target: CostMoney, value: CostMoney): void {
  for (const field of ["gross", "credits", "net"] as const) {
    target[field] += value[field];
    if (!Number.isSafeInteger(target[field])) throw new Error("Cost total exceeds supported precision.");
  }
}
export function monthOffset(month: string, offset: number): string {
  return new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + offset, 1)).toISOString().slice(0, 7);
}
export function resourceSuffixForCost(appId: string): string { return appId.toLowerCase().replace(/[^a-z0-9]/g, "").slice(-20); }
export function budgetStatus(net: number, budget: number | null): "none" | "normal" | "warning" | "exceeded" {
  return budget === null ? "none" : net >= budget ? "exceeded" : net >= budget * 0.8 ? "warning" : "normal";
}
export function costForecast(month: CostMonth, key: string | null, now: number, lastSuccess: number | null): number | null {
  if (!lastSuccess || now - lastSuccess > 48 * 3_600_000 || month.month !== new Date(now).toISOString().slice(0, 7)) return null;
  const end = new Date(lastSuccess); end.setUTCHours(0, 0, 0, 0); end.setUTCDate(end.getUTCDate() - 2);
  const start = new Date(end); start.setUTCDate(start.getUTCDate() - 6);
  const first = start.toISOString().slice(0, 10), last = end.toISOString().slice(0, 10);
  if (first.slice(0, 7) !== month.month || !month.coverageStart || month.coverageStart > first || !month.latestUsageDay || month.latestUsageDay < last) return null;
  let windowNet = 0, throughEnd = 0;
  for (const entity of month.entities) {
    if (key !== null && entity.key !== key) continue;
    for (const day of entity.daily) {
      if (day.day <= last) throughEnd += day.net;
      if (day.day >= first && day.day <= last) windowNet += day.net;
    }
  }
  const days = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)).getUTCDate();
  return Math.round(throughEnd + windowNet / 7 * (days - end.getUTCDate()));
}
export type CostDashboard = {
  months: string[]; selected: CostMonth | null; previous: CostMonth | null;
  lastSuccess: number | null; error: string | null; importing: boolean;
  budgets: { key: string; micros: number }[];
};
